import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { FC, ReactElement, useEffect, useState } from "react";
import { ReactComponent as TableChartView } from "../icons/table_chart_view.svg";
import { ReactComponent as TableChartViewActive } from "../icons/table_chart_view_fill.svg";
import { ReactComponent as MeetingNotes } from "../icons/sticky_note.svg";
import { ReactComponent as MeetingNotesActive } from "../icons/sticky_note_fill.svg";
import { ReactComponent as MeetingMinutes } from "../icons/auto_stories.svg";
import { ReactComponent as MeetingMinutesActive } from "../icons/auto_stories_fill.svg";
import { ReactComponent as MyTasks } from "../icons/task.svg";
import { ReactComponent as MyTasksActive } from "../icons/task_fill.svg";
import { ReactComponent as ProgressDashboard } from "../icons/space_dashboard.svg";
import { ReactComponent as ProgressDashboardActive } from "../icons/space_dashboard_fill.svg";
import { ReactComponent as PMOView } from "../icons/stacks.svg";
import { ReactComponent as PMOViewActive } from "../icons/stacks_fill.svg";
import { ReactComponent as MyProjects } from "../icons/folder_open.svg";
import { ReactComponent as MyProjectsActive } from "../icons/folder_open_fill.svg";
import { ReactComponent as ScheduledEvents } from "../icons/schedule.svg";
import { ReactComponent as ScheduledEventsActive } from "../icons/schedule_fill.svg";
import { ReactComponent as AdminPanelSettings } from "../icons/admin_panel_settings.svg";
import { ReactComponent as AdminPanelSettingsActive } from "../icons/admin_panel_settings_fill.svg";
import { ReactComponent as MenuItemOpenInNew } from "../icons/menuitem_openinnew.svg";
import CoachingHub from "@mui/icons-material/GroupsOutlined";
import CoachingHubActive from "@mui/icons-material/Groups";
import SessionsActive from '@mui/icons-material/CalendarMonth';
import Sessions from '@mui/icons-material/CalendarMonthOutlined';
import Management from "@mui/icons-material/ManageAccountsOutlined";
import ManagementActive from "@mui/icons-material/ManageAccounts";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { UserRole } from "../models";
import React from "react";
import { AppContext } from "../models/applicationState";
import { Context } from "../components/context";
import i18n from "../i18n/config";

const navLinks = [
  {
    index: 1,
    name: "coachingNavigator.coachingHub",
    url: "/coaching-hub",
    icon: "CoachingHubIcon",
    roles: [UserRole.Admin, UserRole.Coach],
  },
  {
    index: 2,
    name: "coachingNavigator.sessions",
    url: "/sessions",
    icon: "SessionsIcon",
    roles: [UserRole.Admin, UserRole.Coach],
  },
  {
    index: 3,
    name: "coachingNavigator.management.managementHeader",
    url: "/management",
    icon: "ManagementIcon",
    roles: [UserRole.Admin, UserRole.Coach],
  },
  {
    index: 4,
    name: "menu.leadership",
    url: "/leadership",
    icon: "LeadershipIcon",
    roles: [UserRole.Admin, UserRole.Creator, UserRole.Actor, UserRole.Coach],
  },
  {
    index: 5,
    name: "menu.create_meeting_note",
    url: "/meetings",
    icon: "EventNoteIcon",
    roles: [UserRole.Admin, UserRole.Creator, UserRole.Actor, UserRole.Coach],
  },
  {
    index: 6,
    name: "menu.meeting_minutes_library",
    url: "/library",
    icon: "LibraryBooksIcon",
    roles: [UserRole.Admin, UserRole.Creator, UserRole.Actor, UserRole.Coach],
  },
  {
    index: 7,
    name: "menu.actor_dashboard",
    url: "/dashboard/actor",
    icon: "TaskIcon",
    roles: [UserRole.Admin, UserRole.Creator, UserRole.Actor, UserRole.Coach],
  },
  {
    index: 8,
    name: "menu.creator_dashboard",
    url: "/dashboard/creator",
    icon: "DashboardIcon",
    roles: [UserRole.Admin, UserRole.Creator, UserRole.Coach],
  },
  {
    index: 9,
    name: "menu.my_tags",
    url: "/tags/all",
    icon: "StacksIcon",
    roles: [UserRole.Admin, UserRole.Creator],
  },
  {
    index: 10,
    name: "menu.manage_projects",
    url: "/projects",
    icon: "FolderIcon",
    roles: [UserRole.Admin],
  },
  {
    index: 11,
    name: "menu.manage_roles",
    url: "/users",
    icon: "AdminPanelSettingsIcon",
    roles: [UserRole.Admin],
  },
  {
    index: 12,
    name: "menu.my_calendar_events",
    url: "/events",
    icon: "ScheduleIcon",
    roles: [UserRole.Admin, UserRole.Creator],
  },
  {
    index: 13,
    name: "menu.my_calendar_events",
    url: "/coaching-calendar",
    icon: "ScheduleIcon",
    roles: [UserRole.Coach],
  },
];

interface NavProps {
  open: boolean;
}

const HoveredBox = styled(Box)({
  display: "flex",
  gap: 0,
  alignItems: "center",
  paddingRight: "4px",
  paddingLeft: "4px",
  borderRadius: "4px",
  backgroundColor: "rgba(124, 138, 197, 0.2)",
  width: "100%",
  marginRight: 0,
});

const DefaultBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  width: "100%",
});

const HoveredBoxNewTab = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 1,
  borderRadius: "4px",
  padding: "6px",
  marginLeft: "4px",
  backgroundColor: "rgba(124, 138, 197, 0.2)",
  height: "auto",
});

const setActiveNav = (path: string): number => {
  const active = navLinks.filter(({ url }) => path.startsWith(url));
  return active[0]?.index;
};

const MainNav: FC<NavProps> = (props: NavProps): ReactElement => {
  const navigate = useNavigate();
  const theme = useTheme();

  const { t } = useTranslation();
  const location = useLocation();

  const [filteredNavLinks, setFilteredNavLinks] = useState<typeof navLinks>([]);
  const [selectedIndex, setSelectedIndex] = useState(
    setActiveNav(location.pathname) || undefined,
  );
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const appContext = React.useContext<AppContext>(Context);

  useEffect(() => {
    setFilteredNavLinks(
      navLinks.filter((l) => l.roles.includes(appContext.state.invoker?.role)),
    );
  }, [appContext.state.invoker]);

  useEffect(() => {
    setSelectedIndex(setActiveNav(location.pathname));
  }, [location.pathname]);

  const handleListItemClick = (event: any, selectedIndex: number) => {
    event.preventDefault();
    setSelectedIndex(selectedIndex);
    const selection = navLinks.filter(
      ({ index }) => index === selectedIndex,
    )[0];
    navigate(selection ? selection.url : "/");
  };

  const navigateToNewTab = (event: any, selectedIndex: number) => {
    event.preventDefault();
    const selection = navLinks.filter(
      ({ index }) => index === selectedIndex,
    )[0];

    const link = document.createElement("a");
    link.href = selection ? selection.url : "/";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
    return navigateToNewTab;
  };

  const getNavIcon = (icon: string, active: boolean): ReactElement => {
    switch (icon) {
      case "LeadershipIcon":
        return active ? (
          <TableChartViewActive color={theme.palette.primary.main} />
        ) : (
          <TableChartView />
        );
      case "ScheduleIcon":
        return active ? (
          <ScheduledEventsActive color={theme.palette.primary.main} />
        ) : (
          <ScheduledEvents />
        );
      case "EventNoteIcon":
        return active ? (
          <MeetingNotesActive color={theme.palette.primary.main} />
        ) : (
          <MeetingNotes />
        );
      case "LibraryBooksIcon":
        return active ? (
          <MeetingMinutesActive color={theme.palette.primary.main} />
        ) : (
          <MeetingMinutes />
        );
      case "TaskIcon":
        return active ? (
          <MyTasksActive color={theme.palette.primary.main} />
        ) : (
          <MyTasks />
        );
      case "DashboardIcon":
        return active ? (
          <ProgressDashboardActive color={theme.palette.primary.main} />
        ) : (
          <ProgressDashboard />
        );
      case "AdminPanelSettingsIcon":
        return active ? (
          <AdminPanelSettingsActive color={theme.palette.primary.main} />
        ) : (
          <AdminPanelSettings />
        );
      case "FolderIcon":
        return active ? (
          <MyProjectsActive color={theme.palette.primary.main} />
        ) : (
          <MyProjects />
        );
      case "StacksIcon":
        return active ? (
          <PMOViewActive color={theme.palette.primary.main} />
        ) : (
          <PMOView />
        );
      case "CoachingHubIcon":
        return active ? (
          <CoachingHubActive
            style={{ color: active ? theme.palette.primary.main : undefined }}
          />
        ) : (
          <CoachingHub />
        );
      case "SessionsIcon":
        return active ? (
          <SessionsActive
            style={{ color: active ? theme.palette.primary.main : undefined }}
          />
        ) : (
          <Sessions />
        );
      case "ManagementIcon":
        return active ? (
          <ManagementActive
            style={{ color: active ? theme.palette.primary.main : undefined }}
          />
        ) : (
          <Management />
        );
      default:
        return <div />;
    }
  };

  const hasTopSection = filteredNavLinks.some(({ index }) => index <= 3);

  return (
    <List component="nav">
      {filteredNavLinks.map(({ index, name, icon }) => {
        const WrapperBox =
          hoveredIndex === index || selectedIndex === index
            ? HoveredBox
            : DefaultBox;

        return (
          <React.Fragment key={index}>
            {index === 4 && hasTopSection && (
              <Divider
                sx={{
                  my: 1.25,
                  mx: props.open ? 1.5 : 0.75,
                  borderColor: "rgba(0, 0, 0, 0.2)",
                  borderBottomWidth: 2,
                }}
              />
            )}
            <ListItem
              disablePadding
              sx={{ display: "flex" }}
              className="nav-list-item"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <ListItemButton
                className="nav-list-item-button"
                sx={{
                  height: 60,
                  justifyContent: props.open ? "initial" : "center",
                  px: 2.5,
                  my: "2px", // moved from hover state to here to fix the spacing issue
                  padding: "0 4px 0 14px",
                  alignItems: "stretch",
                  width: "100%",
                  ":hover": {
                    backgroundColor: "transparent",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  },
                }}
                selected={selectedIndex === index}
              >
                <WrapperBox
                  onClick={(event) => handleListItemClick(event, index)}
                  sx={{
                    justifyContent: !props.open ? "center" : "default",
                    ml: !props.open ? -1.5 : 0,
                  }}
                >
                  <ListItemIcon
                    children={getNavIcon(
                      icon,
                      selectedIndex === index || hoveredIndex === index,
                    )}
                    sx={{
                      minWidth: 0,
                      mr: props.open ? (hoveredIndex !== index ? 3 : 1) : 0,
                      justifyContent: "center",
                    }}
                  />
                  <ListItemText
                    // if the index is 4 and the language is de and the hovered index is the same as the current index, then show the word break
                    primary={
                      index === 6 &&
                      i18n.language === "de" &&
                      hoveredIndex === index
                        ? t("menu.meeting_minutes_library_word_break")
                        : t(name)
                    }
                    sx={[{
                      display: props.open ? "block" : "none",
                      opacity: props.open ? 1 : 0,
                      wordBreak: "break-word"
                    }, hoveredIndex === index ? {
                      whiteSpace: "normal"
                    } : {
                      whiteSpace: "nowrap"
                    }, (selectedIndex === index || hoveredIndex === index) && {
                      fontWeight: "bold",
                      color: "#242C63",
                    }]}
                  />
                </WrapperBox>

                {hoveredIndex === index && props.open && (
                  <HoveredBoxNewTab>
                    <MenuItemOpenInNew
                      onClick={(e) => {
                        e.preventDefault();
                        navigateToNewTab(e, index);
                      }}
                      style={{
                        cursor: "pointer",
                      }}
                    />
                  </HoveredBoxNewTab>
                )}
              </ListItemButton>
            </ListItem>
          </React.Fragment>
        );
      })}
    </List>
  );
};

export default MainNav;
